'use client';

import { c as _c } from "react/compiler-runtime";
import { FILE_AI_NOTICE } from '@shared/all/domains/oakbox';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { client, type RouterOutputs } from '../../utils/trpc';

// Shared types + helpers for the file-vault pages (/onedrive/{search,embed}).

export type Progress = RouterOutputs['onedriveEmbed']['progress'];
export type FileRow = RouterOutputs['onedriveEmbed']['files']['files'][number];
export type FolderRow = RouterOutputs['onedriveEmbed']['folders']['folders'][number];
export type SyncedFolder = RouterOutputs['onedriveEmbed']['syncedFolders']['folders'][number];
// TranscriptSource enum → friendly pill label.
export const SOURCE_LABEL: Record<string, string> = {
  TEXT_LAYER: 'Text layer',
  OCR: 'OCR',
  VISION: 'Vision'
};

// Connection state, shared across the shell + both panels (React Query dedups).
export const useConnection = t0 => {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["onedriveEmbed", "status"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== enabled) {
    t2 = {
      queryKey: t1,
      enabled,
      queryFn: _temp
    };
    $[1] = enabled;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const status = useQuery(t2);
  const t3 = status.data?.connected ?? false;
  const t4 = status.data?.configured ?? true;
  const t5 = status.data?.canWrite ?? true;
  let t6;
  if ($[3] !== status || $[4] !== t3 || $[5] !== t4 || $[6] !== t5) {
    t6 = {
      status,
      connected: t3,
      configured: t4,
      canWrite: t5
    };
    $[3] = status;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
};

// Debounce a value so type-as-you-search doesn't fire a query per keystroke.
export const useDebouncedValue = (value, ms) => {
  const $ = _c(4);
  const [debounced, setDebounced] = useState(value);
  let t0;
  let t1;
  if ($[0] !== ms || $[1] !== value) {
    t0 = () => {
      const id = setTimeout(() => setDebounced(value), ms);
      return () => clearTimeout(id);
    };
    t1 = [value, ms];
    $[0] = ms;
    $[1] = value;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return debounced;
};

// Attach the returned ref to a sentinel at the end of an infinite list; when it
// scrolls into view, `onMore` fires. Re-runs when `enabled` flips (next page
// finished) so a still-visible sentinel keeps paging.
export const useInfiniteScroll = (onMore: () => void, enabled: boolean) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const cb = useRef(onMore);
  cb.current = onMore;
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const io = new IntersectionObserver(entries => entries[0]?.isIntersecting && cb.current(), {
      rootMargin: '200px'
    });
    io.observe(el);
    return () => io.disconnect();
  }, [enabled]);
  return ref;
};

// Shown above every tab that puts files in, so the two facts that matter are on
// screen at the moment of adding rather than only in the privacy notice.
export const VaultAiNotice = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ShieldAlert size={14} className="mt-px shrink-0" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="flex items-start gap-2 rounded-md border border-ds-border-subtle px-3 py-2 text-xs text-textSecondary">{t0}<span>{FILE_AI_NOTICE}{" "}<Link href="/privacy#vaultfiles" target="_blank" className="whitespace-nowrap text-primary hover:underline">How we handle files</Link></span></p>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const Pill = t0 => {
  const $ = _c(3);
  const {
    children,
    accent
  } = t0;
  const t1 = `rounded-full border px-2 py-0.5 text-xs ${accent ? "border-primary/40 bg-primary/10 text-primary" : "border-ds-border-standard bg-background-primary text-textSecondary"}`;
  let t2;
  if ($[0] !== children || $[1] !== t1) {
    t2 = <span className={t1}>{children}</span>;
    $[0] = children;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
async function _temp() {
  const [err, res] = await client.onedriveEmbed.status.query();
  if (err) {
    throw err;
  }
  return res;
}